import { useState, useEffect } from "react";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";
import { Home, Trophy, Star, Info } from "lucide-react";
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  CarouselNext, 
  CarouselPrevious 
} from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { RankCard } from "@/components/ranks/RankCard";
import { getStreakManager } from "@/components/game/managers/StreakManager";
import { getStreakRank, getRankDescription } from "@/lib/streakRanks";
import { Header } from "@/components/layout/Header";

const BabbleRanks = () => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [userStreak, setUserStreak] = useState<number>(0);
  
  useEffect(() => {
    const streakManager = getStreakManager();
    const streakState = streakManager.getStreakState();
    setUserStreak(streakState.currentStreak || 0);
    
    if (streakState.currentStreak && streakState.currentStreak > 0) {
      setCurrentIndex(streakState.currentStreak - 1);
    }
  }, []);

  const getRankTitle = (index: number): string => {
    return getStreakRank(index + 1);
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col relative overflow-x-hidden">
      <SEO
        title="Babble Ranks – BabbleGram Streak Tiers"
        description="Explore BabbleGram streak ranks. Build a daily streak and climb through every BabbleRank tier."
        path="/babbleranks"
      />
      <Header />

      <main className="flex-1 container max-w-4xl mx-auto px-4 py-8 pt-24 relative z-10 overflow-visible">
        <div className="text-center mb-10">
          <div className="flex justify-center items-center mb-4">
            <Trophy className="text-[#de0046] mr-2 h-8 w-8" />
            <h1 className="text-3xl font-bold">Babble<span className="text-[#de0046]">Ranks</span></h1>
          </div>
          
          <div className="max-w-2xl mx-auto space-y-5 text-gray-300">
            <div className="bg-gray-800/50 p-5 rounded-lg space-y-4">
              <div className="flex items-start">
                <Info className="text-[#de0046] h-5 w-5 mr-3 mt-1 flex-shrink-0" />
                <div className="text-left">
                  <p className="text-lg">Daily Mode Streaks: Complete the daily BabbleGram puzzle each day to build your streak—miss a day, and it resets!</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Trophy className="text-[#de0046] h-5 w-5 mr-3 mt-1 flex-shrink-0" />
                <div className="text-left">
                  <p className="text-lg">BabbleRanks: Climb through unique ranks as your daily streak grows, each with its own character and title. Can you reach BabbleGram Grandmaster?</p>
                </div>
              </div>
            </div>
          </div>
          
          {userStreak > 0 && (
            <div className="mt-6 text-center p-4 bg-[#de0046]/10 rounded-lg inline-flex items-center border border-[#de0046]/20">
              <Star className="text-[#de0046] h-6 w-6 mr-3" />
              <div>
                <span className="block">Your current rank: <span className="font-bold text-[#de0046] text-lg">{getStreakRank(userStreak)}</span></span>
                <span className="text-sm text-gray-300">(Day {userStreak} streak)</span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-10 px-4 md:px-6 overflow-visible">
          <Carousel 
            className="w-full overflow-visible" 
            opts={{
              align: "center",
              loop: false,
              startIndex: userStreak > 0 ? userStreak - 1 : 0,
              inViewThreshold: 0.6,
              slidesToScroll: 1
            }}
            setApi={(api) => {
              if (api) {
                api.on("select", () => {
                  setCurrentIndex(api.selectedScrollSnap());
                });
                // Force a reinitialization after a small delay to ensure proper centering
                setTimeout(() => {
                  api.reInit();
                }, 100);
              }
            }}
          >
            <CarouselContent className="overflow-visible -ml-6">
              {Array.from({ length: 30 }).map((_, index) => (
                <CarouselItem key={index} className="md:basis-1/2 lg:basis-2/5 pl-6 overflow-visible">
                  <div className="p-1 h-full overflow-visible relative">
                    <RankCard 
                      index={index + 1} 
                      title={getRankTitle(index)} 
                      description={getRankDescription(index + 1)}
                      isCurrentRank={userStreak === index + 1}
                      showSparkles={userStreak === index + 1}
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>

            <div className="flex items-center justify-between mt-10">
              <CarouselPrevious className="relative left-0 translate-y-0 bg-gray-800 hover:bg-gray-700 border-gray-700" />
              <div className="text-center text-sm">
                <span className="text-[#de0046] font-bold">{currentIndex + 1}</span>
                <span className="text-gray-400"> / 30</span>
              </div>
              <CarouselNext className="relative right-0 translate-y-0 bg-gray-800 hover:bg-gray-700 border-gray-700" />
            </div>
          </Carousel>
        </div>

      </main>
    </div>
  );
};

export default BabbleRanks;
